<template>
  <div class="relative">
    <slot name="badge"></slot>
    <div
      class="z-20 flex flex-col justify-end overflow-hidden border bg-white align-middle transition-colors hover:border-gray-550"
      :class="[
        roundedClass,
        selected ? 'border-black' : 'border-gray-350',
        openable ? 'initial' : 'relative',
      ]"
    >
      <div
        class="flex flex-col justify-between p-5 md:px-8"
        :class="{ 'cursor-pointer': openable && !controlled }"
        @click="toggle"
      >
        <div class="flex w-full items-center gap-3">
          <LazyIconFielmannChevronDown
            v-if="openable && !hideArrow"
            class="size-6 shrink-0 transition"
            :class="{ '-rotate-180': isOpen }"
          />
          <Headline
            v-dompurify-html="title"
            tag="span"
            look="h4"
            class="grow"
          />
          <div class="text-lg font-normal">
            <!-- @slot Use this slot to add something on the right side of the title -->
            <slot name="title-right"></slot>
          </div>
        </div>
        <slot name="under-title"></slot>
      </div>
      <div
        class="z-10 flex flex-col gap-3 rounded-b-lg bg-white"
        :class="{ 'transition-height': openable, relative: openable }"
        :style="isOpen || !openable ? { height: contentHeight } : { height: 0 }"
      >
        <div
          ref="sightdataContent"
          class="flex flex-col gap-5 px-5 pb-5 pt-1 md:px-8 md:pb-6"
        >
          <!-- @slot Slot for the content of the drawer -->
          <slot name="content"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useElementSize } from '@vueuse/core'

const props = defineProps({
  openable: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  title: {
    type: String as PropType<string>,
    required: true,
  },
  startOpen: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  roundedClass: {
    type: String as PropType<string>,
    default: 'rounded-lg',
  },
  selected: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  hideArrow: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  controlled: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
})

const sightdataContent = ref()
const startOpen = toRef(props, 'startOpen')
const isOpen = ref(!!props.startOpen)
const scope = effectScope()
onScopeDispose(() => scope.stop())

scope.run(() => {
  if (import.meta.server) {
    return
  }
  watch(startOpen, (next) => (isOpen.value = next))
})

const toggle = () => {
  if (!props.controlled) {
    isOpen.value = !isOpen.value
  }
}

const { height } = useElementSize(
  sightdataContent,
  { width: 0, height: 0 },
  { box: 'border-box' },
)

const contentHeight = computed(() =>
  height.value ? `${height.value}px` : 'fit-content',
)
</script>
